import { useState, useMemo, useCallback, useEffect } from "react";
import { api } from "../../core/api/queries";

/**
 * Simplified hook for managing program editions
 *
 * Features:
 * - Auto-fetches editions via React Query
 * - Auto-selects current edition on mount
 * - Provides simple changeEdition callback
 * - No manual loading or params management required
 *
 * @example Basic usage
 * ```tsx
 * const { editions, currentEdition, changeEdition } = useEditions();
 *
 * return (
 *     <select value={currentEdition?.id} onChange={(e) => changeEdition(e.target.value)}>
 *         {editions.map(ed => <option key={ed.id} value={ed.id}>{ed.nume}</option>)}
 *     </select>
 * );
 * ```
 */
export function useEditions() {
    const [selectedEditionId, setSelectedEditionId] = useState<string | null>(null);

    /* 🔴 `isError` si `refetch` se EXPUN, nu se ignora. Pana aici se destructurau
       doar `data` si `isLoading`, iar consecinta nu era vizibila in hookul asta:
       `use-report-page` porneste interogarea principala cu
       `enabled: !!currentEdition`, deci o interogare de editii picata lasa
       `currentEdition = null` si raportul NU CERE NICIODATA datele. O interogare
       care n-a rulat are `isLoading: false`, `data: undefined` si `isError: FALSE`
       — asa ajungea ecranul sa afirme „TOTAL … 0" fara nicio eroare.
       Masurat: 3 din cele 4 rapoarte verificate cad pe calea asta, nu pe eroarea
       interogarii proprii. */
    const { data: editions = [], isLoading, isError, refetch } = api.editions.all.useQuery();

    // Auto-select current edition on mount
    useEffect(() => {
        if (editions.length > 0 && !selectedEditionId) {
            const current = editions.find((ed) => ed.isCurrentEdition === true);
            if (current?.id) {
                setSelectedEditionId(current.id);
            }
        }
    }, [editions, selectedEditionId]);

    // Compute current edition from selected ID
    const currentEdition = useMemo(() => {
        if (!selectedEditionId) return null;
        return editions.find((ed) => ed.id === selectedEditionId) || null;
    }, [editions, selectedEditionId]);

    // Callback to change edition
    const changeEdition = useCallback((editionId: string) => {
        setSelectedEditionId(editionId);
    }, []);

    /* Contract EXTINS, nu modificat: `editions`, `currentEdition`, `changeEdition`
       si `isLoading` se comporta exact ca inainte, inclusiv implicitul `[]`. Cei
       trei consumatori din afara rapoartelor nu citesc campurile noi. */
    return {
        editions,
        currentEdition,
        changeEdition,
        isLoading,
        isError,
        retry: refetch
    };
}
